import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import adminApi from '../api/adminApi';
import BilingualField from '../components/BilingualField';

const inputClass = "w-full bg-[#1A1A1A] border border-[rgba(212,175,55,0.2)] rounded-sm px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#D4AF37] transition-colors";
const labelClass = "block text-xs uppercase tracking-wider text-[#D4AF37] font-semibold mb-2";
const Card = ({ title, children }) => (
  <div className="bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm p-5 space-y-4">
    <h2 className="text-sm font-semibold text-white border-b border-[rgba(212,175,55,0.1)] pb-3">{title}</h2>
    {children}
  </div>
);

const ContactEditorPage = () => {
  const [translations, setTranslations] = useState({ en: {}, ar: {} });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get('/admin/content').then(r => {
      setTranslations(r.data?.translations || { en: {}, ar: {} });
    }).catch(() => toast.error('Failed to load content'));
  }, []);

  const set = (lang, path, value) => {
    const keys = path.split('.');
    setTranslations(prev => {
      const updated = { ...prev };
      let obj = updated[lang] = { ...updated[lang] };
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  const bilingual = (label, path) => ({
    label,
    valueEn: path.split('.').reduce((o, k) => o?.[k], translations.en) || '',
    valueAr: path.split('.').reduce((o, k) => o?.[k], translations.ar) || '',
    onChangeEn: v => set('en', path, v),
    onChangeAr: v => set('ar', path, v),
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.patch('/admin/content/translations', translations);
      toast.success('Contact info saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Contact Section</h1>
          <p className="text-white/40 text-sm mt-0.5">Address, phone, email, and business hours</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-black font-semibold text-sm rounded-sm hover:bg-[#C5A028] transition-colors disabled:opacity-50">
          <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <Card title="Section Header">
        <BilingualField {...bilingual('Overline', 'contact.overline')} />
        <BilingualField {...bilingual('Title', 'contact.title')} />
        <BilingualField {...bilingual('Subtitle', 'contact.subtitle')} />
      </Card>

      <Card title="Office Address">
        <BilingualField {...bilingual('Office Title', 'contact.office.title')} />
        <BilingualField {...bilingual('Address Line', 'contact.office.address')} />
        <BilingualField {...bilingual('City / Country', 'contact.office.city')} />
      </Card>

      <Card title="Phone">
        <div>
          <label className={labelClass}>Phone Number (same for both languages)</label>
          <input
            value={translations.en?.contact?.phone?.number || ''}
            onChange={e => { set('en', 'contact.phone.number', e.target.value); set('ar', 'contact.phone.number', e.target.value); }}
            className={inputClass}
            placeholder="+971-4-555-8800"
          />
        </div>
        <BilingualField {...bilingual('Phone Label', 'contact.phone.title')} />
      </Card>

      <Card title="Email">
        <div>
          <label className={labelClass}>Email Address (same for both languages)</label>
          <input
            value={translations.en?.contact?.email?.address || ''}
            onChange={e => { set('en', 'contact.email.address', e.target.value); set('ar', 'contact.email.address', e.target.value); }}
            className={inputClass}
            placeholder="contact@royaltobacco.ae"
          />
        </div>
        <BilingualField {...bilingual('Email Label', 'contact.email.title')} />
      </Card>

      <Card title="Business Hours">
        <BilingualField {...bilingual('Hours Label', 'contact.hours.title')} />
        <BilingualField {...bilingual('Weekdays', 'contact.hours.weekdays')} />
        <BilingualField {...bilingual('Weekend', 'contact.hours.weekend')} />
      </Card>
    </div>
  );
};

export default ContactEditorPage;
